import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import './Login.css';
import iconImage from '../../assets/images/icon.png';
import { buildSessionFromLoginResponse, saveSessions, setActiveSession, upsertSession } from '../../utils/accountSessions';

export default function Login() {
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const API_URL = process.env.REACT_APP_API_URL;
    axios.defaults.withCredentials = true;

    const isAddAccountMode = new URLSearchParams(location.search).get('add') === '1';

    // Check for existing user session on component mount
    useEffect(() => {
        if (isAddAccountMode) {
            return;
        }

        const tryBootstrapFromCookies = async () => {
            try {
                const res = await axios.get(API_URL + 'auth/accounts');
                const accounts = res?.data?.accounts;
                if (Array.isArray(accounts) && accounts.length > 0) {
                    const sessions = accounts.map((a) => ({
                        id: a.id,
                        role: a.role,
                        user: a.user,
                        doctor: a.doctor,
                        addedAt: new Date().toISOString(),
                    }));

                    saveSessions(sessions);
                    setActiveSession(sessions[0].id);

                    const role = sessions[0].role;
                    if (role === 'admin') {
                        navigate('/control/doctor-list');
                    } else if (role === 'test') {
                        navigate('/control/test-information');
                    } else {
                        navigate('/control/appointments');
                    }
                    return true;
                }
            } catch (_) {}
            return false;
        };

        tryBootstrapFromCookies();

        const userInfoRaw = localStorage.getItem("userInfo");
        if (userInfoRaw) {
            let role = null;
            try {
                role = JSON.parse(userInfoRaw).role;
            } catch (_) {}
            if (role === 'admin') {
                navigate('/control/doctor-list');
            } else if (role === 'test') {
                navigate('/control/test-information');
            } else {
                navigate('/control/appointments');
            }
        }
    }, [navigate, API_URL, isAddAccountMode]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        try {
            const response = await axios.post(API_URL +"admin/login", { name, password });

            const session = buildSessionFromLoginResponse(response.data);
            if (session) {
                upsertSession(session);
                setActiveSession(session.id);
            } else {
                localStorage.setItem("userInfo", JSON.stringify({
                    user: response.data.user,
                    doctor: response.data.doctor,
                    role: response.data.role
                }));
            }

            if (window.AndroidInterface && window.AndroidInterface.onLogin) {
                window.AndroidInterface.onLogin("", response.data.role);
            }

            // Register FCM token if it exists in localStorage
            const deviceId = localStorage.getItem('device_id');
            if (deviceId) {
                console.log('Linking device to user:', deviceId);
                try {
                    const registerResp = await fetch(`${API_URL}devices/register/doctor`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            device_id: deviceId,
                        }),
                    });

                    const registerData = await registerResp.json();
                    if (registerResp.ok) {
                        console.log('Device linked to user successfully:', registerData);
                    } else {
                        console.error('Failed to link device:', registerData);
                    }
                } catch (registerErr) {
                    console.error('Error linking device:', registerErr);
                }
            }

            if (response.data.role === 'admin') {
                navigate('/control/doctor-list');
            } else if (response.data.role === 'test') {
                navigate('/control/test-information');
            } else {
                navigate('/control/appointments');
            }
        } catch (err) {
            setError("Invalid credentials:" + err);
        }
    };

    return (
        <div className="login-container" dir="ltr">
            <div className="login-card">
                <div className="login-header">
                    <div className="login-icon">
                        <img src={iconImage} alt="Clinic Icon" className="login-icon-image" />
                    </div>
                    <h1 className="login-title">Login</h1>
                    <p className="login-subtitle">Access the Control Panel</p>
                </div>
                
                <div className="login-body">
                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-triangle"></i>
                            Invalid login credentials. Please try again.
                        </div>
                    )}
                    
                    <form onSubmit={handleLogin}>
                        <div className="login-form-group">
                            <label className="login-form-label">Username</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="login-form-control"
                                    placeholder="Enter your username"
                                    required
                                />
                                <i className="fas fa-user login-input-icon"></i>
                            </div>
                        </div>
                        
                        <div className="login-form-group">
                            <label className="login-form-label">Password</label>
                            <div style={{ position: 'relative' }}>
                                <input 
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="login-form-control"
                                    placeholder="Enter your password"
                                    required
                                />
                                <i className="fas fa-lock login-input-icon"></i>
                            </div>
                        </div>
                        
                        <button type="submit" className="login-btn">
                            <div className="btn-content">
                                <i className="fas fa-sign-in-alt"></i>
                                Login
                            </div>
                        </button>
                        <div className="login-register-prompt" style={{ marginTop: '16px', textAlign: 'center' }}>
                            <span>Don't have an account? </span>
                            <Link to="/control/registration" className="register-link" style={{ color: '#2563eb', fontWeight: 600 }}>
                                Register here
                            </Link>
                        </div>
                        <div className="login-privacy-link" style={{ marginTop: '8px', textAlign: 'center', fontSize: '0.95rem' }}>
                            <Link to="/control/privacy" style={{ color: '#6b7280' }}>
                                Privacy Policy
                            </Link>
                        </div>
                    </form>
                </div>
                
                <div className="login-footer">
                    <p>Aullan Bro - Secure, Advanced, and Easy to Use</p>
                </div>
            </div>
        </div>
    );
}